import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
  Router,
  UrlTree
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ServicesVisitasService } from '@servicesVisitas/services-visitas.service';
import { PermisosService } from './permisos.service';
import { ApplicationService } from 'src/app/application/shared/application.service';
import { Menu } from 'src/app/layout/modelos/menu.model';
import { RUTA_MENU_ADMIN } from 'src/app/layout/servicios/layout.constants';

/**
 * Rutas públicas que no requieren validación de permisos
 */
const RUTAS_PUBLICAS: string[] = [
  '/auth',
  //'/login',
  //'/user'  // Editar perfil es accesible para todos
];

/**
 * Valida si una URL es una ruta pública
 * @param url URL a validar
 * @returns true si es ruta pública
 */
function esRutaPublica(url: string): boolean {
  return RUTAS_PUBLICAS.some((rutaPublica: string) => {
    // Comparación exacta o si la URL comienza con la ruta pública
    return url === rutaPublica || url.startsWith(rutaPublica + '/');
  });
}

/**
 * Guard funcional que valida permisos de acceso basado en el menú del usuario
 * Previene acceso a rutas copiando URLs sin tener los permisos correspondientes
 * @param route Snapshot de la ruta activada
 * @param state Estado del router
 * @returns true si tiene permisos, de lo contrario redirecciona al dashboard
 * @author Sistema Kobra
 */
export const PermisosGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree | Observable<boolean | UrlTree> => {

  const permisosService: PermisosService = inject(PermisosService);
  const router: Router = inject(Router);
  const appService: ApplicationService = inject(ApplicationService);
  const visitas: ServicesVisitasService = inject(ServicesVisitasService);

  // Sin query params y decodificada: el router codifica la ñ (`/cuentas/campa%C3%B1as`) y el menú la guarda tal cual.
  const url: string = decodificarRuta(state.url.split('?')[0]);

  // Permitir acceso a rutas públicas sin validación
  if (esRutaPublica(url)) {
    return true;
  }

  // Obtener el menú desde localStorage
  const menu: string | null = localStorage.getItem('menu');

  // Sin menú (recién iniciada la sesión, p. ej. al abrir un enlace compartido): se carga aquí,
  // porque el layout que lo carga aún no existe; mandar al inicio perdería la URL pedida.
  if (!menu) {
    return visitas.get<Menu[]>(RUTA_MENU_ADMIN).pipe(
      map(res => {
        permisosService.guardarPermisos(res.data ?? []);
        return validarPermiso(url, permisosService, router, appService);
      }),
      catchError(() => of(router.createUrlTree(['/']))),
    );
  }

  return validarPermiso(url, permisosService, router, appService);
};

/** Ruta decodificada; una mal codificada (`%E0`) se compara tal cual en lugar de romper la navegación. */
function decodificarRuta(ruta: string): string {
  try {
    return decodeURIComponent(ruta);
  } catch {
    return ruta;
  }
}

/** Con el menú ya guardado: la ruta pasa o se regresa al dashboard con aviso. */
function validarPermiso(url: string, permisosService: PermisosService, router: Router,
  appService: ApplicationService): boolean | UrlTree {
  const tienePermiso: boolean = permisosService.getPermiso(url);

  if (!tienePermiso) {
    console.warn(`Acceso denegado a la ruta: ${url}`);
    appService.showSnackbar(
      "Aviso",
      "No tienes permisos para acceder a esta sección",
      3000,
      "warning"
    );

    // Redirigir al dashboard
    return router.createUrlTree(['/']);
  }

  return true;
}
